import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AlertRuleAuditResponse } from '@/types/alertDetail';

interface RuleAuditSectionProps {
  query: UseQueryResult<AlertRuleAuditResponse, Error>;
}

/**
 * Section 7: rule-evaluation audit trail (Phase D fired/skipped counts).
 *
 * Wrapped in a default-CLOSED Accordion per the user's PR 2 feedback —
 * investigators don't need this on first view, but it's a click away
 * when they want to know "how often does this rule fire?".
 *
 * If the backend reports `rows_available=false` (Phase D writers haven't
 * begun emitting for this rule yet) the section is hidden entirely
 * rather than rendered empty.
 */
export function RuleAuditSection({ query }: RuleAuditSectionProps): JSX.Element | null {
  if (query.isLoading) {
    return (
      <section aria-labelledby="rule-audit">
        <h2 id="rule-audit" className="text-lg font-semibold">
          Rule audit
        </h2>
        <Skeleton className="mt-3 h-12 w-full" />
      </section>
    );
  }

  if (query.isError) {
    return (
      <section aria-labelledby="rule-audit">
        <h2 id="rule-audit" className="text-lg font-semibold">
          Rule audit
        </h2>
        <p className="mt-2 text-sm text-destructive">
          Failed to load rule audit: {query.error?.message ?? 'unknown error'}
        </p>
      </section>
    );
  }

  const data = query.data;
  if (!data || !data.rows_available) {
    // Hide entirely when Phase D writers haven't begun for this rule.
    return null;
  }

  return (
    <section aria-labelledby="rule-audit">
      <Accordion>
        <AccordionItem value="rule-audit-trail" className="border-t">
          <AccordionTrigger>
            <span id="rule-audit" className="text-lg font-semibold">
              Rule audit · {data.fired_count} fired ·{' '}
              {data.returned_none_count} skipped ·{' '}
              {data.exception_count} errors ·{' '}
              {data.kind_deactivated_count} kind-off
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                Show audit trail
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <p className="mb-3 text-xs text-muted-foreground">
              Rule <span className="font-mono">{data.rule_id}</span> has been
              evaluated {data.total_evaluated.toLocaleString()} times across the
              alert's asset.
            </p>
            <div className="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kind</TableHead>
                    <TableHead className="text-right">Fired</TableHead>
                    <TableHead className="text-right">Returned none</TableHead>
                    <TableHead className="text-right">Exception</TableHead>
                    <TableHead className="text-right">Kind off</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_kind.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-xs text-muted-foreground">
                        No per-kind breakdown available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.by_kind.map((row) => (
                      <TableRow key={row.transaction_kind}>
                        <TableCell className="font-mono text-xs uppercase">
                          {row.transaction_kind}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {row.fired_count.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {row.returned_none_count.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {row.exception_count.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {row.kind_deactivated_count.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {row.total.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}
